import type { AddNoteRequest } from '@contracts/note/AddNoteRequest';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { AddNoteForm } from '@src/notes/AddNoteForm';
import type { AddNoteSchemaType } from '@src/notes/AddNoteSchema';
import { AddNoteSchema } from '@src/notes/AddNoteSchema';
import { NoteParentType } from '@src/notes/domain/Note';
import { NoteContext } from '@src/notes/domain/NoteContext';
import { NoteAssembler } from '@src/notes/NoteAssembler';
import { NoteContextField } from '@src/notes/NoteFields';
import { ModalPermitDescription } from '@src/orders/components/permitList/ModalPermitDescription';
import type { SubmittedPermitApplication } from '@src/orders/domain/Order';
import { CompleteAction } from '@src/permits/completePermit/fields/CompletePermitFields';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
  application: SubmittedPermitApplication;
  onConfirm: (notes: AddNoteRequest) => void;
  context: CompleteAction.FLAG_ERROR | CompleteAction.DENY;
  permitNumber?: string;
}

export const CompletePermitWithNoteModal = ({
  open,
  onClose,
  application,
  context,
  onConfirm,
  permitNumber,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();

  //oxlint-disable-next-line
  const form = useForm<AddNoteSchemaType | any>({
    resolver: yupResolver(AddNoteSchema),
    values: {
      [NoteContextField.fieldName]: [NoteContext.error.valueOf()],
    },
  });

  const title =
    context === CompleteAction.FLAG_ERROR
      ? t('permitApplication.issuedWithError')
      : t('permitApplication.denied');
  const description =
    context === CompleteAction.FLAG_ERROR
      ? t('permitApplication.markedAsWithAgentNotification', {
          value: t('issued_error'),
        })
      : t('permitApplication.markedAsWithAgentNotification', {
          value: t('denied'),
        });

  const onSubmit = (data: AddNoteSchemaType) => {
    onConfirm(
      NoteAssembler.fromForm(
        user.id,
        {
          id: application.application.id,
          type: NoteParentType.PERMIT_APPLICATION,
        },
        data,
      ),
    );
  };

  return (
    <BaseModal open={open} onClose={onClose} title={title}>
      <form>
        <ModalPermitDescription
          jurisdiction={application.permitTypeJurisdiction}
          type={application.permitType}
          stateField={permitNumber ?? '-'}
        />
        <Stack paddingY={theme.spacing['6']}>
          <Typography variant="body2">{description}</Typography>
        </Stack>
        <Stack paddingY={theme.spacing['6']}>
          <AddNoteForm
            control={form.control}
            context={NoteContext.error}
            setValue={form.setValue}
          />
          <Divider style={{ marginTop: theme.spacing['6'] }} />
        </Stack>
        <CloseConfirmModalFooter
          onClose={onClose}
          confirm={form.handleSubmit(onSubmit)}
        />
      </form>
    </BaseModal>
  );
};
